import { Fragment, type ReactNode } from "react";

export type HoverHintDefinitionRow = {
  label: string;
  value: ReactNode;
};

/** Standard key/value grid for entity peek-through hover hints (paperwork, batch details, calendar, …). */
/** Key/value layout that shrink-wraps to the rows. Do not use `1fr` / `min-w-0` here: those fill the 36rem hover-hint cap. */
export const HOVER_HINT_DEFINITION_GRID_CLASS =
  "grid w-max max-w-full grid-cols-[auto_auto] items-baseline gap-x-3 gap-y-1 text-left";

export function HoverHintDefinitionGrid({
  rows,
  className,
}: {
  rows: HoverHintDefinitionRow[];
  className?: string;
}) {
  return (
    <dl className={className ?? HOVER_HINT_DEFINITION_GRID_CLASS}>
      {rows.map((row, index) => (
        <Fragment key={`${row.label}-${index}`}>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{row.label}</dt>
          <dd className="min-w-0 break-words text-xs text-slate-800">{row.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function skuQuantityPeekRows(
  lines: ReadonlyArray<{ sku: string; quantity: number }>,
): HoverHintDefinitionRow[] {
  return lines.map((line) => ({
    label: line.sku,
    value: <span className="tabular-nums">{line.quantity.toLocaleString()}</span>,
  }));
}

function catalogField(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed != null && trimmed !== "" ? trimmed : "—";
}

/** Category / description / variant rows for pack-out and transfer SKU peek-throughs. */
export function skuCatalogPeekRows(fields: {
  category?: string | null;
  description?: string | null;
  variant?: string | null;
}): HoverHintDefinitionRow[] {
  return [
    { label: "Category", value: catalogField(fields.category) },
    { label: "Description", value: catalogField(fields.description) },
    { label: "Variant", value: catalogField(fields.variant) },
  ];
}
